import React, { Fragment, useContext } from 'react';
import parse from 'html-react-parser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UncontrolledTooltip } from 'reactstrap';
import QuestionStatus from './QuestionStatus';
import FormRadioButton from '../../../../shared/components/form/FormRadioButton';
import {
  Question,
  ToolkitOption,
  Answer,
  MultipleChoiceWrapper,
} from '../../../../services/types/Toolkit';
import Checkbox from '../../../../shared/components/CheckBox';
import OperationContext from '../context';
import { tooltipPreventOverflowModifiers } from '../../../../shared/misc/utils';

interface Props {
  questionObj: Question;
  answers: Answer[];
  handleChange: (
    value: number,
    questionId: number,
    multipleChoice: MultipleChoiceWrapper | null
  ) => void;
  disabled?: boolean;
}

const QuestionComp = (props: Props) => {
  const { questionObj, answers, handleChange, disabled = false } = props;
  const { id, number, description, options, footNote, links } = questionObj;
  const { operation } = useContext(OperationContext);

  const GetQuestionValue = (qAnswers: Answer[], questionId: number) => {
    const answer = qAnswers.find((x) => x.questionId === questionId);
    return answer ? answer.value.toString() : '';
  };
  const currentValue = GetQuestionValue(answers, id);
  const answered = !!currentValue;
  const checked = (value: string) => currentValue === value;
  const negativeOptions = options.filter((o) => o.value <= 0 && !operation.isEspf);
  const positiveOptions = options.filter((o) => o.value > 0 || operation.isEspf);
  const selectedOption = questionObj.options.find((x) => x.value.toString() === currentValue);
  const showMultipleChoice =
    questionObj.multipleChoices &&
    questionObj.multipleChoices.length > 0 &&
    selectedOption &&
    selectedOption.value >= questionObj.minValueAllowMultipleChoices;
  const getChoiceIndex = (choiceId: number) => {
    const answer = answers.find((x) => x.questionId === questionObj.id);
    if (answer) {
      return answer.answerMultipleChoices.findIndex((x) => x.multipleChoiceId === choiceId);
    }

    return -1;
  };
  const choiceChecked = (choiceId: number) => getChoiceIndex(choiceId) > -1;
  const getComments = (choiceId: number) => {
    const choiceIndex = getChoiceIndex(choiceId);

    if (choiceIndex >= 0) {
      const answer = answers.find((x) => x.questionId === questionObj.id);
      return answer && answer.answerMultipleChoices
        ? answer.answerMultipleChoices[choiceIndex].comments
        : undefined;
    }

    return undefined;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(parseInt(e.target.value, 10), id, {
      value: parseInt(e.target.value, 10) >= questionObj.minValueAllowMultipleChoices,
      choice: {
        multipleChoiceId: -1,
      },
    });
  };
  const renderAnswers = (rOptions: ToolkitOption[]) =>
    rOptions.map((option, index) => (
      <FormRadioButton
        key={`question_${id}_option_${option.id}`}
        radioValue={option.value.toString()}
        element={{
          id: index.toString(),
          text: option.text,
          checked: checked(option.value.toString()),
          htmlId: `question_${id}_option_${option.id}`,
        }}
        handleChange={handleInputChange}
        disabled={disabled}
      />
    ));

  const styleLink = 'white-space: pre-wrap;word-break: break-all;';

  const tooltipTextTag = "<div class='tooltip-question'>";
  const tooltipDetailTagInit = "<span class='tooltiptext-question'>";
  const tooltipDetailTagEnd = '</span>';
  const tooltipMatch = description.match(`${tooltipDetailTagInit}(.+)${tooltipDetailTagEnd}`);

  const getTooltipText = () => {
    if (tooltipMatch) {
      return tooltipMatch[1];
    }
    return '';
  };

  const getDescriptionWithLinks = () => {
    let desc = description;
    if (links) {
      links.forEach((link) => {
        desc = link.url
          ? desc.replace(
              `{${link.code}}`,
              `<a href="${link.url}" style="${styleLink}" target="_blank">${link.url}</a>`
            )
          : desc;
      });
    }

    if (tooltipMatch) {
      desc = desc.replace(
        tooltipTextTag,
        `<div id=tooltip${questionObj.id} class='tooltip-question'>`
      );
      desc = desc.replace(tooltipMatch[0], '');
    }

    return desc;
  };

  const text = getDescriptionWithLinks();
  return (
    <tr>
      <td className="question-number">
        <QuestionStatus completed={answered}>
          {answered ? <FontAwesomeIcon icon="check" /> : number}
        </QuestionStatus>
      </td>
      <td>
        {tooltipMatch && (
          <UncontrolledTooltip
            className="tooltip-toolkit"
            placement="right"
            target={`tooltip${questionObj.id}`}
            modifiers={tooltipPreventOverflowModifiers}
          >
            {getTooltipText()}
          </UncontrolledTooltip>
        )}
        {parse(text)}
        {footNote && <p className="question_footnote">*{footNote}</p>}
      </td>
      <td style={{ width: '380px' }}>
        <table>
          <tbody>
            <tr>
              {!operation.isEspf && <td className="NA">{renderAnswers(negativeOptions)}</td>}
              <td className="PA">{renderAnswers(positiveOptions)}</td>
            </tr>
            {showMultipleChoice && selectedOption && (
              <tr className="form multiple-choice">
                <td colSpan={2}>
                  <p>Please select one or more options</p>
                  {questionObj.multipleChoices.map((choice) => (
                    <Fragment key={`checkbox_${questionObj.id}_option_${choice.id}`}>
                      <Checkbox
                        onChange={(ids: string, value: boolean) => {
                          handleChange(selectedOption.value, id, {
                            value,
                            choice: {
                              multipleChoiceId: parseInt(ids, 10),
                            },
                          });
                        }}
                        element={{
                          id: choice.id.toString(),
                          text: choice.text,
                          checked: choiceChecked(choice.id),
                          htmlId: `checkbox_${selectedOption.id}_option_${choice.id}`,
                        }}
                        disabled={disabled}
                      />
                      {choiceChecked(choice.id) && choice.commentsRequired && (
                        <div className="form">
                          <div className="form__form-group mb-0">
                            <div className="form__form-group-field">
                              <div className="form__form-group-input-wrap">
                                <input
                                  key={`text_${selectedOption.id}_option_${choice.id}`}
                                  name={`text_${selectedOption.id}_option_${choice.id}`}
                                  className={getComments(choice.id) ? '' : 'error'}
                                  placeholder="Other natural hazard..."
                                  style={{ marginLeft: '10px' }}
                                  type="text"
                                  value={getComments(choice.id) || undefined}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handleChange(selectedOption.value, id, {
                                      value: true,
                                      choice: {
                                        multipleChoiceId: choice.id,
                                        comments: e.target.value,
                                      },
                                    });

                                    e.preventDefault();
                                    e.stopPropagation();

                                    return false;
                                  }}
                                  disabled={disabled}
                                  formNoValidate={disabled}
                                  maxLength={100}
                                  required
                                />
                                {!getComments(choice.id) && (
                                  <span className="form__form-group-error">Required</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

export default QuestionComp;
